import React, { useState, useEffect, useRef, useId } from 'react';
import { createPortal } from 'react-dom';
import { portalLabel, portalsOf } from './publicationModel';
import { loadPublicationHistory } from './publicationApi';
import './PublicationDetails.css';

const money = (value, currency) => {
  if (!(Number(value) > 0)) return 'Consultar precio';
  const code = ['USD', 'U$S', 'US$'].includes(currency) ? 'US$' : ['ARS', 'AR$', '$'].includes(currency) ? 'AR$' : currency || '';
  return `${code} ${Number(value).toLocaleString('es-AR', { maximumFractionDigits: 0 })}`;
};
const date = (value) => value && Number.isFinite(new Date(value).getTime())
  ? new Date(value).toLocaleDateString('es-AR') : 'Sin fecha de verificación';
const safeUrl = (url) => { try { return /^https?:$/.test(new URL(url).protocol) ? url : undefined; } catch { return undefined; } };

export function PortalBadges({ property }) {
  return <span className="publication-portals" aria-label="Portales de publicación">
    {portalsOf(property).map(source => <span key={source} className={`publication-portal publication-portal--${['argenprop', 'zonaprop'].includes(source) ? source : 'otro'}`}>
      <span aria-hidden="true" />{portalLabel(source)}
    </span>)}
  </span>;
}

export default function PublicationDetails({ property, compact = false, showHistory = true, onSelectPublication, selectionPending = false }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const dialog = useRef(null);
  const headingId = useId();
  useEffect(() => {
    if (dialogOpen) dialog.current?.showModal();
  }, [dialogOpen]);
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectionError, setSelectionError] = useState('');
  const [pendingId, setPendingId] = useState(null);
  const canChoose = Boolean(onSelectPublication) && !property.is_historical;
  const choosePublication = async (publication) => {
    setSelectionError('');
    setPendingId(publication.property_id);
    try { await onSelectPublication(property, publication); }
    catch (error) { setSelectionError(error.message || 'No pudimos cambiar el anuncio. Volvé a intentar.'); }
    finally { setPendingId(null); }
  };
  const current = property.is_historical ? property.current_publications : property;
  const publications = current?.publications || [];
  const count = current?.publication_count ?? publications.length;
  const agencies = current?.agency_count || 0;
  const ranges = current?.price_ranges || [];
  const loadHistory = async () => {
    setLoading(true); setError('');
    try { setHistory(await loadPublicationHistory(property.id)); }
    catch { setError('No pudimos consultar el historial. Podés volver a intentar.'); }
    finally { setLoading(false); }
  };
  const summary = `${count ? `${count} ${count === 1 ? 'anuncio' : 'anuncios'}` : property.is_historical ? 'Disponibilidad e historial' : 'Anunciante e historial'}${agencies > 0 ? ` · ${agencies} ${agencies === 1 ? 'inmobiliaria' : 'inmobiliarias'}` : ''}`;
  const advertiserContent = (
      <div className="publication-advertisers">
        {canChoose && publications.length > 1 && <p className="publication-choice-help">Elegí qué inmobiliaria mostrar en el reporte. Se usarán los datos y el precio de ese anuncio; la propiedad cuenta una sola vez.</p>}
        {publications.map(p => <div className={`publication-advertiser${canChoose && p.property_id === property.id ? ' publication-advertiser--selected' : ''}`} key={p.property_id}>
          <div><strong>{p.owner_type?.trim() || 'Anunciante sin identificar'}</strong><PortalBadges property={p} /></div>
          <b>{money(p.price_value, p.price_currency)}</b>
          <span className="publication-verified">{p.last_seen_at ? `Última verificación: ${date(p.last_seen_at)}` : 'Sin fecha de verificación'}</span>
          {safeUrl(p.url) && <a href={safeUrl(p.url)} target="_blank" rel="noopener noreferrer">Ver anuncio ↗</a>}
          {canChoose && publications.length > 1 && <button type="button" className="publication-select"
            aria-pressed={p.property_id === property.id}
            disabled={selectionPending || pendingId !== null || p.property_id === property.id || p.is_active === false}
            onClick={() => choosePublication(p)}>
            {pendingId === p.property_id ? 'Cargando anuncio…' : p.property_id === property.id ? 'Elegido para el reporte' : p.is_active === false ? 'Anuncio retirado' : 'Usar este anuncio en el reporte'}
          </button>}
        </div>)}
        {selectionError && <p className="publication-selection-error" role="alert">{selectionError}</p>}
        {!count && !property.is_historical && <span>{property.owner_type?.trim() || 'Anunciante sin identificar'}</span>}
        {showHistory && <button type="button" className="publication-history-button" onClick={loadHistory} disabled={loading}>
          {loading ? 'Consultando…' : history ? 'Actualizar historial' : 'Ver historial de todos los anuncios'}
        </button>}
        {error && <p role="alert">{error}</p>}
        {history && <div className="publication-history" tabIndex={0} aria-label="Historial de precios por anunciante">
          <p>Incluye anuncios retirados y precios guardados por el scraper. Cada precio pertenece a su anunciante.</p>
          {history.length === 0 ? <p>Sin precios históricos registrados.</p> : <table>
            <thead><tr><th>Fecha</th><th>Anunciante</th><th>Precio observado</th></tr></thead>
            <tbody>{history.map((p, i) => <tr key={`${p.property_id}-${p.recorded_at}-${p.observation_source}-${i}`}>
              <td>{date(p.recorded_at)}</td><td>{p.owner_type || 'Sin identificar'}<small>{portalLabel(p.source)} · #{p.property_id}{p.is_active ? '' : ' · retirado'}</small></td>
              <td>{money(p.price_value, p.price_currency)}<small>{p.observation_source === 'property_snapshot' ? 'Fila histórica' : 'Registro de precio'}</small></td>
            </tr>)}</tbody>
          </table>}
        </div>}
      </div>
  );
  return <div className="publication-details" onClick={event => event.stopPropagation()} onKeyDown={event => event.stopPropagation()}>
    <PortalBadges property={current || property} />
    {!compact && (canChoose || property.is_historical) && <p className="publication-report-choice">
      {property.is_historical ? 'Anuncio guardado en el reporte: ' : 'Anuncio para el reporte: '}
      <strong>{property.owner_type?.trim() || 'Anunciante sin identificar'}</strong>
      {' · '}{money(property.price, property.price_currency)}
    </p>}
    {property.is_historical && <p className="publication-snapshot">Comparable guardado · se conserva su precio original</p>}
    {property.availability === 'withdrawn' && <p className="publication-status publication-status--withdrawn">
      Anuncio retirado{property.group_availability === 'active' ? ' · disponible con otros anunciantes' : ' · sin anuncios activos'}
    </p>}
    {property.is_historical && property.availability === 'unknown' && <p className="publication-status">Disponibilidad actual sin verificar</p>}
    {ranges.filter(r => r.min !== r.max).map(r => <p className="publication-range" key={r.currency}>
      {property.is_historical ? 'Hoy: ' : 'Entre anunciantes: '}{money(r.min, r.currency)} – {money(r.max, r.currency)}
    </p>)}
    {ranges.some(r => r.min !== r.max) && !property.is_historical && !compact && <p className="publication-reference">La tasación usa el precio del anuncio {canChoose ? 'elegido para el reporte' : 'de referencia'}, sin promediar estos avisos.</p>}
    {compact ? <>
      <button type="button" className="publication-open" onClick={() => setDialogOpen(true)}>{summary}<span aria-hidden="true">↗</span></button>
      {dialogOpen && createPortal(<dialog ref={dialog} className="publication-dialog" aria-labelledby={headingId}
        onCancel={() => setDialogOpen(false)} onClick={event => { event.stopPropagation(); if (event.target === event.currentTarget) setDialogOpen(false); }}>
        <div className="publication-dialog__heading"><div><h2 id={headingId}>{summary}</h2><p>{property.address || property.location || 'Anuncios del inmueble'}</p></div>
          <button type="button" onClick={() => setDialogOpen(false)} aria-label="Cerrar anunciantes">×</button></div>
        <div className="publication-details">{advertiserContent}</div>
      </dialog>, document.body)}
    </> : <details><summary>{summary}<span className="publication-chevron" aria-hidden="true">⌄</span></summary>{advertiserContent}</details>}
  </div>;
}
